import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { usePageMeta } from '../lib/usePageMeta';
import { MailX, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UnsubscribePage = () => {
  usePageMeta(
    'Unsubscribe — ResiAX',
    'Unsubscribe from ResiAX blog update emails.'
  );
  const [params] = useSearchParams();
  const [state, setState] = useState('loading');

  useEffect(() => {
    window.scrollTo(0, 0);
    const email = params.get('email');
    const sig = params.get('sig');
    if (!email || !sig) {
      setState('invalid');
      return;
    }
    fetch(`${BACKEND_URL}/api/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sig }),
    })
      .then((r) => setState(r.ok ? 'done' : 'invalid'))
      .catch(() => setState('invalid'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-4 sm:px-6 text-center">
          {state === 'loading' && (
            <div data-testid="unsubscribe-loading" className="space-y-4">
              <Loader2 className="w-10 h-10 text-[#B5541B] animate-spin mx-auto" />
              <p className="text-[#3B3535]/70">Updating your preferences…</p>
            </div>
          )}
          {state === 'done' && (
            <div data-testid="unsubscribe-success" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 space-y-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F1E1E]">You're unsubscribed</h1>
              <p className="text-sm text-[#3B3535]/70 leading-relaxed">
                You won't receive blog update emails from ResiAX any more. You may still
                receive essential emails about anything you've registered for, such as
                pilot links you request.
              </p>
              <p className="text-sm text-[#3B3535]/70">
                Changed your mind? You can re-subscribe any time via the forms on our site.
              </p>
              <Link
                to="/"
                data-testid="unsubscribe-home-link"
                className="inline-block text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors"
              >
                Back to resiax.com
              </Link>
            </div>
          )}
          {state === 'invalid' && (
            <div data-testid="unsubscribe-invalid" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 space-y-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-7 h-7 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F1E1E]">This link isn't valid</h1>
              <p className="text-sm text-[#3B3535]/70 leading-relaxed">
                The unsubscribe link looks incomplete or has been altered. Please use the
                link exactly as it appears in your email, or contact us and we'll remove
                you manually.
              </p>
              <a
                href="mailto:Shax@teqie.online?subject=Unsubscribe%20request"
                data-testid="unsubscribe-contact-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors"
              >
                <MailX className="w-4 h-4" />
                Email us to unsubscribe
              </a>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UnsubscribePage;
